<template>
    <loader class="main" :full-screen="true" :loaded="loaded">
        <div class="style-guide">
            <!-- Spacing and layout -->
            <div class="container mx-auto px-2">
                <h1>Spacing &amp; Layout</h1>

                <h3>Big gap - 160px</h3>
                <div class="clear--lg styleGuide--spacers my-4"></div>

                <h3>Medium gap - 80px</h3>
                <div class="clear--md styleGuide--spacers my-4"></div>

                <h3>Small gap - 40px</h3>
                <div class="clear--sm styleGuide--spacers my-4"></div>

                <h3>Tiny gap - 20px</h3>
                <div class="clear--xs styleGuide--spacers my-4"></div>
            </div>

            <div class="clear--md"></div>

            <!-- Background and dividers -->
            <div class="container mx-auto px-2 text-center lg:text-left">
                <h1>Background &amp; Dividers</h1>

                <div class="flex">
                    <div class="w-full lg:w-1/2">
                        <p>
                            We generally use solid white backgrounds. To divide a page into sections we can either rely on whitespace or put one of the sections on the paper texture.
                        </p>
                    </div>
                </div>
            </div>

            <div class="divider--container">
                <div class="pl-8 pr-8 pt-16 pb-16 container mx-auto text-center">
                    <h1>The order of the section styles from front to back goes:</h1>
                    <p>Solid white background with ripped paper edges.</p>
                    <p>Most of the site should be on this colour.</p>
                </div>
            </div>

            <div class="styleGuide__photography-layer divider--container">
                <div class="divider-white--top"></div>
                <div class="container mx-auto h-full flex justify-center items-center text-white text-center">
                    <h3>Optional photography layer</h3>
                </div>
                <div class="divider-paper--bottom"></div>
            </div>
            <div class="pl-8 pr-8 pt-16 pb-20 divider--container background--paper">
                <div class="container mx-auto flex justify-center items-center text-center">
                    <p>
                        Paper texture with ripped edges.<br/>
                        These sections will feel more recessive, so if you're putting important information here
                        make sure to give it lots of space.
                    </p>
                </div>
                <div class="divider-white--bottom"></div>
            </div>
            <div class="px-8 py-16">
                <div class="container mx-auto flex justify-center items-center text-center">
                    <p>
                        Back up to white.
                    </p>
                </div>
            </div>

            <div class="clear--md"></div>

            <!-- Typography -->
            <div>
                <div class="container mx-auto px-4">
                    <h1>Typography</h1>
                    <!-- H1 -->
                    <div class="heading-container">
                        <div class="w-full">
                            <h1>H1</h1>
                            <span class="text-2xl font-thin">Image overlays</span>
                        </div>
                    </div>
                    <div class="flex my-4">
                        <div class="w-3/5">Font-face</div>
                        <div class="w-1/5 text-center">Font-size</div>
                        <div class="w-1/5 text-center">Leading</div>
                    </div>
                    <div class="flex my-4 mb-10">
                        <div class="w-3/5 text-xl font-thin">Freddie's Flowers Title</div>
                        <div class="w-1/5 text-xl font-thin text-center">40px</div>
                        <div class="w-1/5 text-xl font-thin text-center">40px</div>
                    </div>

                    <!-- H2 -->
                    <div class="heading-container">
                        <div class="w-full">
                            <h2>H2</h2>
                            <span class="text-2xl font-thin">Section headings</span>
                        </div>
                    </div>
                    <div class="flex my-4">
                        <div class="w-3/5">Font-face</div>
                        <div class="w-1/5 text-center">Font-size</div>
                        <div class="w-1/5 text-center">Leading</div>
                    </div>
                    <div class="flex my-4 mb-10">
                        <div class="w-3/5 text-xl font-thin">Freddie's Flowers Title</div>
                        <div class="w-1/5 text-xl font-thin text-center">30px</div>
                        <div class="w-1/5 text-xl font-thin text-center">35px</div>
                    </div>

                    <!-- H3 -->
                    <div class="heading-container">
                        <div class="w-full">
                            <h3>H3</h3>
                            <span class="text-2xl font-thin">Body copy on image overlays</span>
                        </div>
                    </div>
                    <div class="flex my-4">
                        <div class="w-2/5">Font-face</div>
                        <div class="w-1/5 text-center">Font-style</div>
                        <div class="w-1/5 text-center">Font-size</div>
                        <div class="w-1/5 text-center">Leading</div>
                    </div>
                    <div class="flex my-4 mb-10">
                        <div class="w-2/5 text-xl font-thin">Gill Sans</div>
                        <div class="w-1/5 text-xl font-thin text-center">Semibold</div>
                        <div class="w-1/5 text-xl font-thin text-center">20px</div>
                        <div class="w-1/5 text-xl font-thin text-center">25px</div>
                    </div>

                    <!-- H4 -->
                    <div class="heading-container">
                        <div class="w-full">
                            <h4>H4</h4>
                            <span class="text-2xl font-thin">Microcopy</span>
                        </div>
                    </div>
                    <div class="flex my-4">
                        <div class="w-2/5">Font-face</div>
                        <div class="w-1/5 text-center">Font-style</div>
                        <div class="w-1/5 text-center">Font-size</div>
                        <div class="w-1/5 text-center">Leading</div>
                    </div>
                    <div class="flex my-4 mb-10">
                        <div class="w-2/5 text-xl font-thin">Gill Sans</div>
                        <div class="w-1/5 text-xl font-thin text-center">Light</div>
                        <div class="w-1/5 text-xl font-thin text-center">15px</div>
                        <div class="w-1/5 text-xl font-thin text-center">15px</div>
                    </div>

                    <!-- H5 -->
                    <div class="heading-container">
                        <div class="w-full">
                            <h5>H5</h5>
                            <span class="text-2xl font-thin">Nav Elements</span>
                        </div>
                    </div>
                    <div class="flex my-4">
                        <div class="w-2/5">Font-face</div>
                        <div class="w-1/5 text-center">Font-style</div>
                        <div class="w-1/5 text-center">Font-size</div>
                        <div class="w-1/5 text-center">Leading</div>
                    </div>
                    <div class="flex my-4 mb-10">
                        <div class="w-2/5 text-xl font-thin">Gill Sans</div>
                        <div class="w-1/5 text-xl font-thin text-center">Regular</div>
                        <div class="w-1/5 text-xl font-thin text-center">10px</div>
                        <div class="w-1/5 text-xl font-thin text-center">15px</div>
                    </div>

                    <!-- P -->
                    <div class="heading-container">
                        <div class="w-full">
                            <p>P</p>
                            <span class="text-2xl font-thin">Body copy, form labels and placeholders</span>
                        </div>
                    </div>
                    <div class="flex my-4">
                        <div class="w-2/5">Font-face</div>
                        <div class="w-1/5 text-center">Font-style</div>
                        <div class="w-1/5 text-center">Font-size</div>
                        <div class="w-1/5 text-center">Leading</div>
                    </div>
                    <div class="flex my-4 mb-10">
                        <div class="w-2/5 text-xl font-thin">Gill Sans</div>
                        <div class="w-1/5 text-xl font-thin text-center">Light</div>
                        <div class="w-1/5 text-xl font-thin text-center">20px</div>
                        <div class="w-1/5 text-xl font-thin text-center">25px</div>
                    </div>
                </div>
            </div>

            <!-- Form elements -->
            <div class="container mx-auto px-2">
                <h1 class="mx-2 mb-2">Form Elements</h1>

                <!-- Input fields -->
                <div class="w-full">
                    <div class="flex flex-wrap">
                        <!-- input -->
                        <div class="input md:w-1/2">
                            <label class="input__label" for="label1">
                                Field Label
                            </label>
                            <input class="input__field" id="label1" type="text" placeholder="Placeholder">
                        </div>

                        <!-- input -->
                        <div class="input md:w-1/2">
                            <label class="input__label" for="label2">
                                Field Label
                            </label>
                            <input
                                class="input__field"
                                id="label2"
                                type="text"
                                placeholder="Placeholder"
                                value="Filled">
                        </div>

                        <!-- input valid -->
                        <div class="input md:w-1/2 input__group--success">
                            <label class="input__label" for="label3">
                                Field Label
                            </label>
                            <input
                                class="input__field"
                                id="label3"
                                type="text"
                                placeholder="Placeholder"
                                value="Completed">
                        </div>

                        <!-- input invalid error -->
                        <div :class="invalidClass">
                            <label class="input__label" for="label4">
                                Invalid input
                            </label>
                            <input
                                class="input__field"
                                id="label4"
                                type="text"
                                placeholder="Placeholder"
                                v-model="inputs.invalid">
                            <p v-show="inputs.invalid.length" class="input__message">Validation message.</p>
                        </div>

                        <!-- input valid -->
                        <div :class="validClass">
                            <label class="input__label" for="label5">
                                Valid input
                            </label>
                            <input
                                class="input__field"
                                id="label5"
                                type="text"
                                placeholder="Placeholder"
                                v-model="inputs.valid">
                            <p v-show="inputs.valid.length" class="input__message">Success message.</p>
                        </div>
                    </div>

                    <div class="flex flex-wrap">
                        <!-- Dropdown -->
                        <div class="input md:w-1/2">
                            <label class="input__label" for="dropdown">
                                Select an address
                            </label>
                            <select class="input__dropdown" id="dropdown">
                                <option :key="item" v-for="item in dropdownArray" v-text="item"></option>
                            </select>
                        </div>
                    </div>
                    <div class="flex flex-wrap">
                        <!-- Radio buttons -->
                        <div class="input md:w-1/2">
                            <label class="input__label">
                                Radio buttons
                            </label>
                            <div class="input__radio">
                                <input
                                    class="input__radio-item"
                                    id="option1"
                                    type="radio"
                                    name="radio"
                                    value="option1">
                                <label for="option1" class="input__radio-label">Option 1</label>
                            </div>
                            <div class="input__radio">
                                <input
                                    class="input__radio-item"
                                    id="option2"
                                    type="radio"
                                    name="radio"
                                    checked="checked"
                                    value="option1">
                                <label for="option2" class="input__radio-label">Option 2</label>
                            </div>
                            <div class="input__radio">
                                <input
                                    class="input__radio-item"
                                    id="option3"
                                    type="radio"
                                    name="radio"
                                    value="option3">
                                <label for="option3" class="input__radio-label">Option 3</label>
                            </div>
                        </div>
                        <!-- Checkboxes -->
                        <div class="input md:w-1/2">
                            <label class="input__label">
                                Checkboxes
                            </label>
                            <div class="input__checkbox">
                                <input
                                    class="input__checkbox-item"
                                    id="check"
                                    type="checkbox"
                                    name="checkbox"
                                    value="true">
                                <label for="check" class="input__checkbox-label">Checkbox unchecked</label>
                            </div>
                            <div class="input__checkbox">
                                <input
                                    class="input__checkbox-item"
                                    id="uncheck"
                                    type="checkbox"
                                    checked="checked"
                                    name="checkbox">
                                <label for="uncheck" class="input__checkbox-label">Checkbox checked</label>
                            </div>
                        </div>
                        <div class="py-4">
                            <div class="input">
                                <label class="inputSwitch">
                                    <input type="checkbox" value="1">
                                    <span class="inputSwitch__slider inputSwitch__slider--round"></span>
                                </label>
                                <label class="input__checkbox--label">Toggle</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Buttons -->
            <div class="container mx-auto px-2">
                <h1 class="mx-2 mb-2">Buttons</h1>
                <div class="flex flex-wrap">
                    <div class="w-full my-1 text-center">
                        <button class="btn btn--primary btn--hoverable">
                            Call to Action - Hoverable
                        </button>
                    </div>
                    <div class="w-full my-1 text-center">
                        <button class="btn btn--primary">
                            Call to Action
                        </button>
                    </div>
                    <div class="w-full my-1 text-center">
                        <button class="btn btn--secondary btn--hoverable">
                            Secondary CTA - Hoverable
                        </button>
                    </div>
                    <div class="w-full my-1 text-center">
                        <button class="btn btn--secondary">
                            Secondary CTA
                        </button>
                    </div>
                    <div class="w-full sm:w-1/2 my-1 text-center">
                        <button class="btn btn--primary btn--auto-width">
                            Auto width
                        </button>
                    </div>
                    <div class="w-full sm:w-1/2 my-1 text-center">
                        <button class="btn btn--secondary btn--auto-width">
                            Auto width
                        </button>
                    </div>
                    <div class="w-full py-1 px-0 md:px-1 md:py-0 md:w-1/2 text-center">
                        <button class="btn btn--primary btn--full-width">
                            Auto width
                        </button>
                    </div>
                    <div class="w-full py-1 px-0 md:px-1 md:py-0 md:w-1/2 text-center">
                        <button class="btn btn--secondary btn--full-width">
                            Auto width
                        </button>
                    </div>
                    <div class="w-full py-1 px-0 md:px-1 md:py-0 md:w-1/2 text-center">
                        <button class="btn btn--primary btn--hoverable btn--full-width">
                            Auto width
                        </button>
                    </div>
                    <div class="w-full py-1 px-0 md:px-1 md:py-0 md:w-1/2 text-center">
                        <button class="btn btn--secondary btn--hoverable btn--full-width">
                            Auto width
                        </button>
                    </div>
                </div>
            </div>

            <div class="clear--lg"></div>

            <div class="clear--lg"></div>

            <!-- modals -->
            <div class="container mx-auto">
                <!-- Normal modal -->
                <button class="btn btn--primary" @click="showModal = true">Show Modal</button>
                <modal v-if="showModal === true" @closed="showModal = false">
                    <div class="text-center">
                        <p>So, to confirm we will be sending 2 boxes a week to 12 Effingham Lane, London, SW3 4UA.</p>
                        <p>Can you confirm this is correct?</p>
                    </div>
                    <template slot="actions-prefix">
                        <button @click.prevent="showModal = false" class="btn btn--secondary mr-2">Yes</button>
                    </template>
                </modal>

                <!-- Full screen -->
                <button class="btn btn--primary md:ml-1" @click="showSecondModal = true">Show Modal</button>
                <modal v-if="showSecondModal === true" @closed="showSecondModal = false"
                       :enable-full-screen="true"
                       :allow-cancel="false">
                    <div style="position: relative;width: 100%;height: 0;padding-bottom: 56.25%;">
                        <iframe src="https://www.youtube.com/embed/dQw4w9WgXcQ?&autoplay=1"
                                frameborder="0"
                                height="100"
                                width="100%"
                                allow="autoplay; encrypted-media">
                        </iframe>
                    </div>
                </modal>
            </div>
        </div>
    </loader>
</template>
<script>
import Modal from '../components/structure/Modal';
import Loader from '../components/structure/Loader';

export default {
    name: 'StyleGuide',
    data() {
        return {
            inputs: {
                invalid: 'Wrong',
                valid: 'Completed',
            },
            showModal: false,
            showSecondModal: false,
            dropdownArray: [
                'Please select an address...',
                '10 Eddiscombe Road, , , , , London,',
                '12 Eddiscombe Road, , , , , London,',
                '14 Eddiscombe Road, , , , , London,',
                '16 Eddiscombe Road, , , , , London,',
                '18 Eddiscombe Road, , , , , London,',
                '2 Eddiscombe Road, , , , , London,',
                '20 Eddiscombe Road, , , , , London,',
                '22 Eddiscombe Road, , , , , London,',
                '24 Eddiscombe Road, , , , , London,',
                '26 Eddiscombe Road, , , , , London,',
                '28 Eddiscombe Road, , , , , London,',
                '30 Eddiscombe Road, , , , , London,',
                '32 Eddiscombe Road, , , , , London,',
                '4 Eddiscombe Road, , , , , London,',
                '6 Eddiscombe Road, , , , , London,',
                '8 Eddiscombe Road, , , , , London,',
                'Ground Floor Flat, 24 Eddiscombe Road, , , , London,',
            ],
            loaded: false,
        };
    },
    computed: {
        invalidClass() {
            return this.inputs.invalid ? 'input md:w-1/2 input--invalid' : 'input md:w-1/2';
        },

        validClass() {
            return this.inputs.valid ? 'input md:w-1/2 input--success' : 'input md:w-1/2';
        },
    },
    mounted() {
        setTimeout(() => {
            this.loaded = true;
        }, 2000);
    },
    components: {
        Loader,
        Modal,
    },
};
</script>
